import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bmoApi } from '../services/api';
import { colors } from '../theme';
import { Card, StatCard, ProgressBar, SectionHeader, Spinner, AlertBanner, EmptyState } from '../components/common';

const MANDATORY = ['Hepatitis B', 'COVID-19'];

export default function VaccinationPage() {
  const navigate = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    bmoApi.getVaccinationSummary()
      .then(setData)
      .catch((e) => setError(e?.message || 'Could not load vaccination data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner size={48} />;
  if (error)   return <AlertBanner type="error" message={error} />;

  const facilities = data?.facilities || [];
  const compliant  = facilities.filter((f) => f.d84_compliant).length;

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ margin:'0 0 4px', fontSize:24, fontWeight:700, color: colors.text.primary }}>💉 Vaccination (D8.4)</h1>
        <p style={{ margin:0, fontSize:14, color: colors.text.secondary }}>Staff immunisation compliance — Kayakalp 2024 D8.4 requirement</p>
      </div>

      <div style={{ background: colors.primary[50], border:`1px solid ${colors.primary[200]}`, borderRadius:8, padding:'12px 16px', marginBottom:20, fontSize:13, color: colors.primary[800] }}>
        ℹ️ <strong>D8.4 (2024 addition):</strong> All clinical and housekeeping staff must have completed Hepatitis B (3 doses) and COVID-19 (2 doses) vaccination series. Medical exemptions must be documented.
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:16, marginBottom:24 }}>
        <StatCard icon="🏥" label="Facilities"        value={facilities.length} color={colors.primary[700]} />
        <StatCard icon="✅" label="D8.4 Compliant"   value={compliant}         color={colors.success[700]} bg={colors.success[50]} />
        <StatCard icon="⚠️" label="Incomplete"        value={facilities.length - compliant} color={colors.error[700]} bg={colors.error[50]} />
      </div>

      <Card style={{ padding:0 }}>
        <div style={{ padding:'14px 20px', borderBottom:`1px solid ${colors.divider}` }}>
          <SectionHeader title="Facility Vaccination Compliance" subtitle="Mandatory vaccines: Hepatitis B + COVID-19" />
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background: colors.neutral[50] }}>
                {['Facility','Total Staff','Hep B Complete','COVID-19 Complete','D8.4 Status'].map((h) => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:600, color: colors.text.secondary, textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {facilities.length === 0
                ? <tr><td colSpan={5}><EmptyState icon="💉" title="No data" /></td></tr>
                : facilities.map((f) => (
                    <tr key={f.id} onClick={() => navigate(`/facilities/${f.id}`)} style={{ cursor:'pointer', borderBottom:`1px solid ${colors.neutral[100]}` }}>
                      <td style={{ padding:'12px 16px', fontSize:14, fontWeight:500, color: colors.text.primary }}>{f.name}</td>
                      <td style={{ padding:'12px 16px', fontSize:13, color: colors.text.secondary }}>{f.total_staff ?? '—'}</td>
                      <td style={{ padding:'12px 16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <ProgressBar pct={f.hep_b_pct ?? 0} />
                          <span style={{ fontSize:13, fontWeight:600, minWidth:40 }}>{Math.round(f.hep_b_pct ?? 0)}%</span>
                        </div>
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <ProgressBar pct={f.covid_pct ?? 0} />
                          <span style={{ fontSize:13, fontWeight:600, minWidth:40 }}>{Math.round(f.covid_pct ?? 0)}%</span>
                        </div>
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:99, background: f.d84_compliant ? colors.success[50] : colors.error[50], color: f.d84_compliant ? colors.success[700] : colors.error[700] }}>
                          {f.d84_compliant ? '✓ Compliant' : '✗ Incomplete'}
                        </span>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
